import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { formatUZS } from '@/lib/format';
import { buildTrend, type Granularity, type SaleLike } from '@/lib/report-metrics';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ChartTooltipBox, compactUZS } from './chart-bits';

const OPTIONS: { value: Granularity; labelKey: string; buckets: number }[] = [
  { value: 'day', labelKey: 'tick_reports.daily', buckets: 30 },
  { value: 'week', labelKey: 'tick_reports.weekly', buckets: 12 },
  { value: 'month', labelKey: 'tick_reports.monthly', buckets: 12 },
];

export function SalesTrendChart({ sales }: { sales: SaleLike[] }) {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('day');
  const option = OPTIONS.find((o) => o.value === granularity)!;
  const data = buildTrend(sales, granularity, option.buckets);

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="text-sm font-medium text-foreground">{t('tick_reports.trendTitle')}</h3>
        <ToggleGroup
          type="single"
          value={granularity}
          onValueChange={(v) => v && setGranularity(v as Granularity)}
          className="bg-muted/50 rounded-lg p-0.5"
        >
          {OPTIONS.map((o) => (
            <ToggleGroupItem key={o.value} value={o.value} className="text-xs px-3 h-7">
              {t(o.labelKey)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis yAxisId="sum" tick={{ fontSize: 11 }} tickFormatter={compactUZS} width={56} />
            <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11 }} width={32} />
            <Tooltip
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <ChartTooltipBox
                    title={String(label)}
                    rows={[
                      { label: t('tick_reports.sale'), value: formatUZS(Number(payload[0]?.payload?.sum ?? 0)) },
                      { label: t('tick_reports.colTransactions'), value: t('tick_reports.count', { n: payload[0]?.payload?.count ?? 0 }) },
                    ]}
                  />
                ) : null
              }
            />
            <Area
              yAxisId="sum"
              type="monotone"
              dataKey="sum"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              fill="url(#trendFill)"
            />
            <Line
              yAxisId="count"
              type="monotone"
              dataKey="count"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        {t('tick_reports.trendLegend')}
      </p>
    </div>
  );
}
